import { CLEFS, CLEF_INFO, type Clef, type StaffRegion } from './clef.ts';

/**
 * Skill catalog. Skill IDs are stable identifiers persisted in the database;
 * never rename one — add a new skill and migrate evidence instead.
 */
export type SkillFamily = 'note-identification' | 'rhythm';

export interface SkillDefinition {
  id: string;
  family: SkillFamily;
  name: string;
  description: string;
  clef?: Clef;
}

export type NoteIdSkillKind = 'lines' | 'spaces' | 'ledger-below' | 'ledger-above';

export function clefSkillId(clef: Clef, kind: NoteIdSkillKind): string {
  return `nid.${clef}.${kind}`;
}

export const SKILL_ACCIDENTAL_SHARPS = 'nid.accidentals.sharps';
export const SKILL_ACCIDENTAL_FLATS = 'nid.accidentals.flats';
export const SKILL_OCTAVE_NAMING = 'nid.octaves';

/** Rhythm skills: the meter's pulse plus each rhythmic feature a phrase can contain. */
export const SKILL_RHYTHM_SIMPLE = 'rhy.pulse.simple';
export const SKILL_RHYTHM_COMPOUND = 'rhy.pulse.compound';
export const SKILL_RHYTHM_DIVISIONS = 'rhy.divisions';
export const SKILL_RHYTHM_SUBDIVISIONS = 'rhy.subdivisions';
export const SKILL_RHYTHM_DOTTED = 'rhy.dotted';
export const SKILL_RHYTHM_RESTS = 'rhy.rests';
export const SKILL_RHYTHM_TIES = 'rhy.ties';
export const SKILL_RHYTHM_TRIPLETS = 'rhy.triplets';

const KIND_TEXT: Record<NoteIdSkillKind, { name: string; description: string }> = {
  lines: { name: 'line notes', description: 'Naming notes on the five staff lines.' },
  spaces: { name: 'space notes', description: 'Naming notes in the staff spaces, including just above and below the staff.' },
  'ledger-below': { name: 'ledger lines below', description: 'Naming notes written with ledger lines below the staff.' },
  'ledger-above': { name: 'ledger lines above', description: 'Naming notes written with ledger lines above the staff.' },
};

function buildSkills(): SkillDefinition[] {
  const skills: SkillDefinition[] = [];
  for (const clef of CLEFS) {
    for (const kind of Object.keys(KIND_TEXT) as NoteIdSkillKind[]) {
      const clefName = CLEF_INFO[clef].name;
      skills.push({
        id: clefSkillId(clef, kind),
        family: 'note-identification',
        clef,
        name: `${clefName}: ${KIND_TEXT[kind].name}`,
        description: KIND_TEXT[kind].description,
      });
    }
  }
  skills.push(
    {
      id: SKILL_ACCIDENTAL_SHARPS,
      family: 'note-identification',
      name: 'Reading sharps',
      description: 'Naming notes written with a sharp sign.',
    },
    {
      id: SKILL_ACCIDENTAL_FLATS,
      family: 'note-identification',
      name: 'Reading flats',
      description: 'Naming notes written with a flat sign.',
    },
    {
      id: SKILL_OCTAVE_NAMING,
      family: 'note-identification',
      name: 'Octave naming',
      description: 'Naming notes with scientific octave numbers (middle C is C4).',
    },
  );
  skills.push(
    { id: SKILL_RHYTHM_SIMPLE, family: 'rhythm', name: 'Rhythm in simple meters', description: 'Tapping rhythms in time in 2/4, 3/4, 4/4, 5/4, 6/4 and 2/2.' },
    { id: SKILL_RHYTHM_COMPOUND, family: 'rhythm', name: 'Rhythm in compound meters', description: 'Tapping rhythms felt in dotted-quarter beats: 6/8, 9/8 and 12/8.' },
    {
      id: SKILL_RHYTHM_DIVISIONS,
      family: 'rhythm',
      name: 'Beat divisions',
      description: 'Notes that start part-way through a beat: the “and” in simple meters, the second or third eighth in compound meters.',
    },
    { id: SKILL_RHYTHM_SUBDIVISIONS, family: 'rhythm', name: 'Beat subdivisions', description: 'Notes on the finer subdivision of the beat, such as sixteenth notes.' },
    { id: SKILL_RHYTHM_DOTTED, family: 'rhythm', name: 'Dotted rhythms', description: 'Long–short patterns written with dotted notes.' },
    { id: SKILL_RHYTHM_RESTS, family: 'rhythm', name: 'Rests', description: 'Counting through silences without tapping.' },
    { id: SKILL_RHYTHM_TIES, family: 'rhythm', name: 'Ties and syncopation', description: 'Holding tied notes across beats and barlines instead of tapping them again.' },
    { id: SKILL_RHYTHM_TRIPLETS, family: 'rhythm', name: 'Triplets', description: 'Three even notes in the time of two: eighth-note and quarter-note triplets.' },
  );
  return skills;
}

export const SKILLS: readonly SkillDefinition[] = buildSkills();

export const SKILL_BY_ID: ReadonlyMap<string, SkillDefinition> = new Map(SKILLS.map((s) => [s.id, s]));

export function skillName(id: string): string {
  return SKILL_BY_ID.get(id)?.name ?? id;
}

export function regionSkillKind(region: StaffRegion, isLine: boolean): NoteIdSkillKind {
  if (region === 'ledger-below') return 'ledger-below';
  if (region === 'ledger-above') return 'ledger-above';
  return isLine ? 'lines' : 'spaces';
}
